import Breadcrumbs from "components/Breadcrumbs";
import GeneralComponents from "components/GeneralComponents";
import HorizontalScroll from "components/HorizontalScroll";
import ImageWithText from "components/ImageWithText";
import Modal from "components/Modal";
import SEO from "components/SEO";
import Text from "components/Text";
import { greybackground, memberStyle, pagePadding } from "data/commonClasses";
import { fetchParallel, getTranslationContent } from "functions";
import { isArray, isEmpty } from "lodash";
import dynamic from "next/dynamic";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import useTranslation from "translations/hooks/useTranslation";

const DynamicCollapse = dynamic(import("components/collapse"), {
  ssr: false,
});

const SyndicateOverview = ({
  data,
  lang,
  branches,
  committees,
  previousCouncils,
  allPositions,
}) => {
  const { t } = useTranslation();

  let [selectedYear, setSelectedYear] = useState(null);

  let selectedCouncilYear = selectedYear
    ? previousCouncils?.find((oneMem) => oneMem?.title == selectedYear)
    : {};

  var breadcrumbs = [
    { text: t("home"), href: "/" },
    { text: t("syndicateOverview") },
  ];

  const findInData = (key) => {
    return (data || [])?.find((oneData) => oneData?.key == key);
  };
  let councilExtraData = findInData("presidents-from-establishment");
  const renderCollapse = ({ title, subtitle, records }) => {
    return (
      <div className={twMerge(greybackground, "p-0 py-5 px-5 md:px-14")}>
        <Text text={t(title)} sectionTitle bold />
        {subtitle && subtitle !== "" ? (
          <Text text={t(subtitle)} className="pt-5" />
        ) : null}
        <div className="flex flex-col my-5 gap-5">
          {records &&
            isArray(records) &&
            records.map((oneRecord) => (
              <DynamicCollapse
                key={oneRecord?._id}
                className={"bg-white w-full"}
                lang={lang}
                title={oneRecord?.title || ""}
              >
                <div className="h-[1px] w-full self-end bg-black" />
                <div
                  className="py-4 px-10 text-black font-[JannaLT-Regular] leading-8"
                  dangerouslySetInnerHTML={{
                    __html: oneRecord?.description || "",
                  }}
                />
              </DynamicCollapse>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`${pagePadding} mb-10`}>
      <Breadcrumbs items={breadcrumbs} />
      <SEO
        title={t("syndicateOverview")}
        description={t("syndicateOverview")}
      />
      <ImageWithText lang={lang} data={findInData("syndicate-overview")} />
      <div className={twMerge(greybackground, "p-0 py-5")}>
        <GeneralComponents
          allPositions={allPositions}
          lang={lang}
          membersProps={findInData("current-council")}
        />
        <GeneralComponents
          allPositions={allPositions}
          lang={lang}
          membersProps={findInData("retirement-council")}
        />
      </div>
      {renderCollapse({
        title: "branches",
        subtitle: "branchesSubtitle",
        records: branches,
      })}
      {renderCollapse({ title: "associations", records: committees })}
      {previousCouncils && previousCouncils?.length ? (
        <div className={twMerge(greybackground, "p-5 my-5 px-0")}>
          <Text
            sectionTitle
            text={t("previousCouncils")}
            className="px-14"
            bold
          />
          <HorizontalScroll>
            {previousCouncils?.map((oneData, index) => {
              return (
                <div
                  onClick={() => {
                    setSelectedYear(oneData?.title);
                    typeof document != "undefined" &&
                      document?.getElementById("YearModal")?.showModal();
                  }}
                  className={twMerge(memberStyle, "cursor-pointer py-4")}
                  key={`${oneData?._id}-${index}`}
                >
                  <Text bold direction={"ltr"} white text={oneData?.title} />
                </div>
              );
            })}
          </HorizontalScroll>
        </div>
      ) : null}
      {councilExtraData && !isEmpty(councilExtraData) ? (
        <DynamicCollapse
          className={"bg-white shadow-md w-full "}
          lang={lang}
          title={
            getTranslationContent(councilExtraData, "title", lang) ||
            councilExtraData?.title ||
            ""
          }
        >
          <div className="h-[1px] w-full self-end bg-black" />
          <div
            className=" leading-8 text-black"
            dangerouslySetInnerHTML={{
              __html:
                getTranslationContent(councilExtraData, "content", lang) || "",
            }}
          />
        </DynamicCollapse>
      ) : null}
      <Modal
        id={"YearModal"}
        lang={lang}
        onClose={() => {
          setSelectedYear(null);
        }}
        component={
          <div className="flex items-center flex-col">
            <div className={twMerge(memberStyle, "w-full md:w-3/4 my-2")}>
              <Text white center text={selectedYear} />
            </div>
            <div className="w-full md:w-3/4 flex items-center flex-col">
              {selectedCouncilYear && !isEmpty(selectedCouncilYear) ? (
                <div className="flex items-center w-3/4">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: selectedCouncilYear?.description,
                    }}
                    className="mx-3 text-black"
                  />
                </div>
              ) : null}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default SyndicateOverview;

export async function getServerSideProps(context) {
  let { lang } = context?.query;
  let body = {
    key: [
      "syndicate-overview",
      "retirement-council",
      "current-council",
      "presidents-from-establishment",
    ],
    lang,
  };
  let requests = [
    {
      key: "data",
      apiName: "oea-pages-content",
      reqBody: body,
    },

    {
      key: "branches",
      apiName: "oea-branches/get",
      reqBody: { lang },
    },
    {
      key: "committees",
      apiName: "oea-committees/get",
      reqBody: { lang },
    },
    {
      key: "previousCouncils",
      apiName: "oeaPreviousCouncils/get",
      reqBody: { lang },
    },
    {
      key: "allPositions",
      apiName: "get-oea-members-positions",
      reqBody: { lang },
    },
  ];
  let fetchRes = await fetchParallel(requests);
  let data = fetchRes?.data?.response;
  let branches = fetchRes?.branches?.response;
  let committees = fetchRes?.committees?.response;
  let previousCouncils = fetchRes?.previousCouncils?.response;
  let allPositions = fetchRes?.allPositions?.response;

  return {
    props: {
      data,
      lang,
      branches,
      committees,
      previousCouncils,
      allPositions,
    },
  };
}
